import { useMemo } from "react";
import { HStack, Icon } from "@chakra-ui/react";
import { generatePath } from "react-router";
import { createColumnHelper } from "@tanstack/react-table";
import { FiUserX } from "react-icons/fi";
import { API_ROUTES, SHARED_ROUTES } from "definitions/constants/routeConstants";
import apiClient from "services/ApiClient";
import { ResponseModel } from "services/ApiClient/responseModel";
import { User } from "features/User/models";
import { basicShortDate } from "helpers/time";
import { getAvailableRolesOptions } from "features/Role/helpers";
import { userKeys } from "features/User/hooks";
import { InternalLink } from "components/Link";
import GenericTable, { GenericTableProps, simpleColumn } from "./GenericTable";
import WorkforceTableRowMenu from "./WorkforceTableRowMenu";
import { WorkforceTableFiltersType, DEFAULT_WORKFORCE_SORT } from "./WorkforceTableFilters";

const columnHelper = createColumnHelper<User>();

const createColumns = (
  onEditProfile?: (userId: string) => void,
  onUpdateRoles?: (userId: string) => void,
  onDeactivate?: (userId: string) => void,
) => [
  columnHelper.accessor((row) => row, {
    id: "name",
    header: "Name",
    cell: (row) => {
      const user = row.getValue();
      const isDeactivated = !!user.deactivatedAt;

      return (
        <HStack spacing={2}>
          {isDeactivated && <Icon as={FiUserX} color="red.500" boxSize={4} />}
          <InternalLink
            to={generatePath(SHARED_ROUTES.showUser, { id: user.id || "" })}
            textDecoration={isDeactivated ? "line-through" : undefined}
            color={isDeactivated ? "gray.500" : undefined}
          >
            {user.fullName}
          </InternalLink>
        </HStack>
      );
    },
    enableSorting: false,
  }),
  simpleColumn({ attribute: "title", label: "Title" }, columnHelper, false),
  simpleColumn({ attribute: "workEmail", label: "Work Email" }, columnHelper, false),
  columnHelper.accessor("permission", {
    cell: (info) =>
      info.getValue()
        ? getAvailableRolesOptions(info.getValue())
            .map((value) => value.label)
            .join(", ")
        : "",
    header: "Roles",
    enableSorting: false,
  }),
  columnHelper.accessor((row) => row, {
    id: "createdAt",
    header: "Added",
    cell: (row) => basicShortDate(row.getValue().createdAt),
    enableSorting: false,
  }),
  // Actions Column (Three Dots Menu)
  columnHelper.accessor((row) => row, {
    id: "actions",
    header: "",
    cell: ({ getValue }) => {
      const user = getValue();
      return (
        <WorkforceTableRowMenu
          user={user}
          onEditProfile={onEditProfile}
          onUpdateRoles={onUpdateRoles}
          onDeactivate={onDeactivate}
        />
      );
    },
    meta: {
      sx: {
        width: "60px",
        minWidth: "60px",
      },
      headerHStackProps: { justifyContent: "flex-end" },
      cellHStackProps: { justifyContent: "flex-end" },
    },
    enableSorting: false,
  }),
];

interface WorkforceTableProps {
  filters?: WorkforceTableFiltersType;
  onEditProfile?: (userId: string) => void;
  onUpdateRoles?: (userId: string) => void;
  onDeactivate?: (userId: string) => void;
}

export default function WorkforceTable({
  filters = {},
  onEditProfile,
  onUpdateRoles,
  onDeactivate,
}: WorkforceTableProps) {
  const sort = filters.sort || DEFAULT_WORKFORCE_SORT;

  const additionalQueries = useMemo(() => {
    const queries: Record<string, string> = {};
    queries.roles = String(true);

    // Add search query
    if (filters.q && filters.q.length > 2) {
      queries.q = filters.q;
    }

    // Add show_deactivated filter
    if (filters.show_deactivated === "true") {
      queries.show_deactivated = "true";
    }

    // Add sort parameters
    if (sort) {
      const parts = sort.split("_");
      const direction = parts[parts.length - 1];
      const sortBy = parts.slice(0, -1).join("_");

      queries["sort[0][sort_by]"] = sortBy;
      queries["sort[0][direction]"] = direction;
    }

    return queries;
  }, [filters.q, filters.show_deactivated, sort]);

  const columns = useMemo(
    () => createColumns(onEditProfile, onUpdateRoles, onDeactivate),
    [onEditProfile, onUpdateRoles, onDeactivate],
  );

  const tableProps: GenericTableProps<User> = {
    fetchData: (query, signal) =>
      apiClient
        .performRequest<ResponseModel<User[]>>(`${API_ROUTES.users.base}${query}`, undefined, signal)
        .then((response) => response.payload),
    // Generic table adds the filter to the end of this automatically
    queryKey: (queryString) => userKeys.list(queryString),
    columns,
    meta: { pagination: { perPage: 100 } },
    additionalQueries,
  };

  return <GenericTable {...tableProps} />;
}
